import type { ApolloClient } from "@apollo/client/core";

import jobsByIdQuery from "@/apollo/queries/v2/JobsById.gql";
import type { JobNotification } from "@/store/modules/jobs";

/**
 * Generates an id for a job before it exists.
 *
 * Some mutations run their work inline in the API lambda, which has a far longer budget than the
 * gateway will wait for a response. Generating the id up front means the client can keep polling the
 * job even when the mutation call itself never comes back. The value has to be valid ObjectId hex
 * because the backend uses it as the job document's _id.
 */
export const generateJobId = () => {
    if (window.crypto?.getRandomValues) {
        const bytes = new Uint8Array(12);
        window.crypto.getRandomValues(bytes);
        return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
    }

    return Array.from({ length: 24 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
};

/**
 * Polls for a job to turn up, so a caller can tell a job that has genuinely started from one that was
 * never created, and so a mutation rejection can be distinguished from a gateway timeout.
 *
 * Deliberately queries `jobs` rather than `job`: the latter throws NotFoundError for an id that does not
 * exist yet, and the global Apollo error link broadcasts every query error to the app, which surfaces a
 * spurious "cannot be found" message while we are still waiting. `jobs` returns an empty list instead.
 */
export const waitForJobToExist = async (
    apolloClient: Pick<ApolloClient<unknown>, "query">,
    jobId: string,
    attempts = 5,
    waitMs = 1000
) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
            const { data } = await apolloClient.query<{ jobs: (JobNotification | null)[] | null }>({
                fetchPolicy: "no-cache",
                query: jobsByIdQuery,
                variables: { jobIds: [jobId] }
            });

            if (data?.jobs?.some(job => job?._id === jobId)) {
                return true;
            }
        } catch {
            if (attempt === attempts - 1) {
                return false;
            }
        }

        await new Promise(resolve => {
            window.setTimeout(resolve, waitMs);
        });
    }

    return false;
};
